import { WaypointStatusName, WaypointType } from '@caresend/types';
import { hasStatus } from '@caresend/utils';
import { defineComponent, computed, ref, watch } from 'vue';
import __vue_component__$1 from '../../../../buttons/ButtonComponent/ButtonComponent.vue.js';
import __vue_component__ from '../../../../cards/BasicCard/BasicCard.vue.js';
import __vue_component__$2 from './components/ItineraryWaypointActionsContainerCard.vue.js';
import __vue_component__$3 from './components/ItineraryWaypointCardStatuses.vue.js';
import __vue_component__$4 from './components/ItineraryWaypointInfoCard.vue.js';
import __vue_component__$5 from './components/ItineraryWaypointLocationAndDurationCard.vue.js';
import __vue_component__$6 from '../Workflow/InlineWorkflowItem.vue.js';
import { usePortalStore } from '../../store/portal/index.js';
import { removeWaypointActionsAndMaybeWaypoint } from '../../store/portal/set/index.js';
import { useItineraryWorkflowsStore } from '../../store/portal/workflow/itinerary.js';
import { workflowUtils } from '../../store/portal/workflow/utils.js';
import TransitionExpand from '../../../../transitions/TransitionExpand/TransitionExpand.js';
import __vue_component__$7 from '../../../../ui/Skeleton.vue.js';
import '@sentry/vue';
import '@fortawesome/fontawesome-svg-core';
import '@fortawesome/sharp-regular-svg-icons';
import 'buefy';
import '../../../../ui/Icon.vue.js';
import '../../../../../functions/configuration/icons.js';
import '../../../../../database/algoliaConfig/indices.js';
import { preventParentClickAndDefault } from '../../../../../functions/preventDefault.js';
import 'mobile-device-detect';
import '../../../../../functions/tracking/index.js';
import { getStore } from '../../../../../store/helpers/initStore.js';

var script = defineComponent({
    name: 'ItineraryWaypointCard',
    components: {
        BasicCard: __vue_component__,
        ButtonComponent: __vue_component__$1,
        ItineraryWaypointActionsContainerCard: __vue_component__$2,
        TransitionExpand,
        ItineraryWaypointCardStatuses: __vue_component__$3,
        ItineraryWaypointInfoCard: __vue_component__$4,
        ItineraryWaypointLocationAndDurationCard: __vue_component__$5,
        InlineWorkflow: __vue_component__$6,
        Skeleton: __vue_component__$7,
    },
    props: {
        dwPreview: { type: String, default: () => '' },
        inputDisabled: { type: Boolean },
        collapsible: { type: Boolean, default: true },
        hideWorkflow: { type: Boolean, default: false },
        waypoint: { type: Object, default: () => null },
        waypointID: { type: String, default: () => '' },
        isCollapsed: { type: Boolean, default: true },
        suggestedWaypointIDs: { type: Array, default: () => null },
        statusOverride: { type: Object, default: () => null },
        enableAccessInstructions: { type: Boolean, default: false },
        statusOverrideLoading: { type: Boolean, default: false },
    },
    emits: [
        'openWaypointDetails',
        'deleteWaypoint',
        'addToItinerary',
        'setIsCollapsed',
        'hoverStart',
        'hoverEnd',
    ],
    setup(props, { emit }) {
        const store = getStore();
        const portalStore = usePortalStore();
        const { viewAs } = portalStore;
        const itineraryWorkflowsStore = useItineraryWorkflowsStore();
        const containerClasses = computed(() => portalStore.state.value.colorScheme === 'dark' ? ['ItineraryWaypointCard--dark'] : []);
        const hideCard = computed(() => !!props.dwPreview
            && !!portalStore.itinerary.getters.draftWaypointIDs()[props.dwPreview]);
        const waypointIndex = computed(() => { var _a; return portalStore.itinerary.getters.waypointIndexByID(((_a = props.waypoint) === null || _a === void 0 ? void 0 : _a.id) || ''); });
        const waypointInItinerary = computed(() => waypointIndex.value !== -1);
        const storeLoading = computed(() => store.state.app.loadingIDs);
        const isLoading = computed(() => { var _a, _b, _c; return (_c = (_a = storeLoading.value) === null || _a === void 0 ? void 0 : _a[(_b = props.waypointID) !== null && _b !== void 0 ? _b : '']) !== null && _c !== void 0 ? _c : false; });
        const waypointActions = computed(() => { var _a, _b; return props.waypoint ? (_b = (_a = portalStore.waypoints.get(props.waypoint.id)) === null || _a === void 0 ? void 0 : _a.computed.waypointActions) !== null && _b !== void 0 ? _b : [] : []; });
        const openWaypointDetails = () => {
            if (!waypointInItinerary.value)
                return;
            if (!props.waypoint)
                return;
            const { id } = props.waypoint;
            itineraryWorkflowsStore.state.set.waypointID(id);
        };
        const deleteWaypoint = () => (waypointInItinerary.value && props.waypoint)
            ? removeWaypointActionsAndMaybeWaypoint(undefined, props.waypoint.id)
            : null;
        const isActionable = computed(() => {
            var _a, _b;
            if (!props.waypoint)
                return false;
            return (_b = (_a = portalStore.waypoints.get(props.waypoint.id)) === null || _a === void 0 ? void 0 : _a.computed.isActionable) !== null && _b !== void 0 ? _b : false;
        });
        const nextWorkflowItem = computed(() => {
            var _a, _b, _c;
            if (!props.waypoint)
                return;
            const workflow = (_b = (_a = portalStore.waypoints.get(props.waypoint.id)) === null || _a === void 0 ? void 0 : _a.computed.workflows[portalStore.itinerary.value.id]) === null || _b === void 0 ? void 0 : _b.default;
            if (!workflow)
                return;
            return (_c = workflowUtils.getNextActionable(workflow)) === null || _c === void 0 ? void 0 : _c.id;
        });
        const isCardCollapsed = ref(props.isCollapsed);
        const toggleCollapse = () => {
            isCardCollapsed.value = !isCardCollapsed.value;
            emit('setIsCollapsed', isCardCollapsed.value);
        };
        watch(() => props.isCollapsed, (newVal) => {
            isCardCollapsed.value = newVal;
        });
        const isSuperCollapsed = computed(() => props.collapsible
            && isCardCollapsed.value && hasStatus(props.waypoint, WaypointStatusName.COMPLETE));
        watch(() => { var _a; return (_a = props.waypoint) === null || _a === void 0 ? void 0 : _a.status; }, () => {
            if (props.collapsible && hasStatus(props.waypoint, WaypointStatusName.COMPLETE)) {
                isCardCollapsed.value = true;
            }
        }, { immediate: true });
        /** TODO: Implement `ItineraryWaypointActionsContainerCard` collapsible view for pick-drop waypoints */
        const canCollapse = computed(() => {
            var _a, _b, _c, _d;
            return (props.collapsible
                && (hasStatus(props.waypoint, WaypointStatusName.COMPLETE)
                    || (((_a = props.waypoint) === null || _a === void 0 ? void 0 : _a.type) !== WaypointType.PICK_DROP
                        && ((_d = (_c = (_b = props.waypoint) === null || _b === void 0 ? void 0 : _b.waypointActions) === null || _c === void 0 ? void 0 : _c.length) !== null && _d !== void 0 ? _d : 0) > 1)));
        });
        const suggestOpen = computed(() => {
            var _a, _b, _c;
            if (!isCardCollapsed.value)
                return false;
            return (isActionable.value
                && nextWorkflowItem.value === 'waypointActions'
                && ((_c = (_b = (_a = props.waypoint) === null || _a === void 0 ? void 0 : _a.waypointActions) === null || _b === void 0 ? void 0 : _b.length) !== null && _c !== void 0 ? _c : 0) > 1);
        });
        const itineraryWaypointActionsContainerCardClasses = computed(() => {
            const classNames = [];
            if (suggestOpen.value) {
                classNames.push('ItineraryWaypointCard__waypoint-actions--suggest-open');
            }
            return classNames;
        });
        const workflow = computed(() => {
            var _a, _b;
            if (!props.waypoint)
                return;
            return (_b = (_a = portalStore.waypoints.get(props.waypoint.id)) === null || _a === void 0 ? void 0 : _a.computed.workflows[portalStore.itinerary.value.id]) === null || _b === void 0 ? void 0 : _b.default;
        });
        return {
            canCollapse,
            containerClasses,
            deleteWaypoint,
            hideCard,
            isLoading,
            isActionable,
            itineraryWaypointActionsContainerCardClasses,
            isCardCollapsed,
            isSuperCollapsed,
            nextWorkflowItem,
            openWaypointDetails,
            preventParentClickAndDefault,
            storeLoading,
            toggleCollapse,
            suggestOpen,
            waypointActions,
            waypointInItinerary,
            waypointIndex,
            workflow,
            viewAs,
        };
    },
});

export { script as default };
